import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { timeOffAPI } from '../services/api';

export const useTimeOff = () => {
  const queryClient = useQueryClient();

  // Get all time off requests
  const {
    data: timeOffRequests = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['timeOff'],
    queryFn: timeOffAPI.getAll,
  });

  // Create time off request mutation
  const createTimeOff = useMutation({
    mutationFn: timeOffAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeOff'] });
      queryClient.invalidateQueries({ queryKey: ['timeOffCalendar'] });
      queryClient.invalidateQueries({ queryKey: ['timeOffBalance'] });
    },
  });

  // Update time off request mutation
  const updateTimeOff = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      timeOffAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeOff'] });
      queryClient.invalidateQueries({ queryKey: ['timeOffCalendar'] });
      queryClient.invalidateQueries({ queryKey: ['timeOffBalance'] });
    },
  });

  // Delete time off request mutation
  const deleteTimeOff = useMutation({
    mutationFn: timeOffAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeOff'] });
      queryClient.invalidateQueries({ queryKey: ['timeOffCalendar'] });
      queryClient.invalidateQueries({ queryKey: ['timeOffBalance'] });
    },
  });

  return {
    timeOffRequests,
    isLoading,
    error,
    refetch,
    createTimeOff: createTimeOff.mutate,
    updateTimeOff: updateTimeOff.mutate,
    deleteTimeOff: deleteTimeOff.mutate,
    isCreating: createTimeOff.isPending,
    isUpdating: updateTimeOff.isPending,
    isDeleting: deleteTimeOff.isPending,
  };
};

export const useTimeOffCalendar = () => {
  const {
    data: calendarData = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['timeOffCalendar'],
    queryFn: timeOffAPI.getCalendar,
  });

  return {
    calendarData,
    isLoading,
    error,
    refetch,
  };
};

export const useTimeOffBalance = () => {
  const {
    data: balance,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['timeOffBalance'],
    queryFn: () => timeOffAPI.getBalance(),
  });

  return {
    balance,
    isLoading,
    error,
    refetch,
  };
};

export const useTimeOffBalances = () => {
  const queryClient = useQueryClient();

  const {
    data: balances = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['timeOffBalances'],
    queryFn: timeOffAPI.getBalances,
  });

  // Update balance mutation
  const updateBalance = useMutation({
    mutationFn: ({ employeeId, data }: { employeeId: string; data: any }) => 
      timeOffAPI.updateBalance(employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeOffBalances'] });
    },
  });

  return {
    balances,
    isLoading,
    error,
    refetch,
    updateBalance: updateBalance.mutate,
    isUpdating: updateBalance.isPending,
  };
};
